import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, CheckCircle2, AlertCircle, Loader2, UserCheck, UserX, RefreshCw } from 'lucide-react';

const ImportCSVModal = ({
    isOpen,
    onClose,
    status, // 'reading' | 'importing' | 'completed' | 'error'
    progress, // { current, total, currentName }
    summary, // { created, skipped, errors, errorMessage }
    onFinish
}) => {
    if (!isOpen) return null;

    const percentage = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
    const isProcessing = status === 'reading' || status === 'importing';

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-card backdrop-blur-2xl border border-border rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-xl ${
                                status === 'completed' 
                                    ? 'bg-emerald-500/10 text-emerald-500' 
                                    : status === 'error'
                                    ? 'bg-red-500/10 text-red-500'
                                    : 'bg-primary/10 text-primary'
                            }`}>
                                {isProcessing ? (
                                    <Loader2 size={24} className="animate-spin" />
                                ) : status === 'completed' ? (
                                    <CheckCircle2 size={24} />
                                ) : status === 'error' ? (
                                    <AlertCircle size={24} />
                                ) : (
                                    <Upload size={24} />
                                )}
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-foreground">Importation de membres</h3>
                                <p className="text-xs text-muted-foreground">
                                    {status === 'reading' && 'Analyse de la structure et de l\'encodage du fichier...'}
                                    {status === 'importing' && `Traitement en cours (${progress.current}/${progress.total})`}
                                    {status === 'completed' && 'Importation terminée et liste actualisée'}
                                    {status === 'error' && 'Une erreur est survenue pendant l\'importation'}
                                </p>
                            </div>
                        </div>
                        {!isProcessing && (
                            <button
                                onClick={() => {
                                    if (onFinish) onFinish();
                                    onClose();
                                }}
                                className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-muted rounded-full"
                            >
                                <X size={20} />
                            </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Status: Reading / Parsing */}
                        {status === 'reading' && (
                            <div className="py-8 flex flex-col items-center justify-center text-center space-y-4">
                                <Loader2 size={40} className="text-primary animate-spin" />
                                <div>
                                    <p className="text-foreground font-semibold">Lecture du fichier CSV...</p>
                                    <p className="text-xs text-muted-foreground mt-1">Détection automatique de l'encodage (UTF-8 / Windows-1252)</p>
                                </div>
                            </div>
                        )}

                        {/* Status: Importing */}
                        {status === 'importing' && (
                            <div className="space-y-5">
                                <div>
                                    <div className="flex items-center justify-between text-sm font-medium mb-2">
                                        <span className="text-foreground truncate max-w-[280px]">
                                            {progress.currentName ? `Création de : ${progress.currentName}` : 'Traitement en cours...'}
                                        </span>
                                        <span className="text-primary font-bold">{percentage}%</span>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full h-3 bg-muted rounded-full overflow-hidden border border-border">
                                        <motion.div
                                            className="h-full bg-gradient-to-r from-primary to-primary/80"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percentage}%` }}
                                            transition={{ duration: 0.2 }}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-muted/40 border border-border p-3 rounded-2xl text-center">
                                        <span className="text-xs text-muted-foreground block">Total</span>
                                        <span className="text-lg font-bold text-foreground">{progress.total}</span>
                                    </div>
                                    <div className="bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-2xl text-center">
                                        <span className="text-xs text-emerald-500 font-medium block">Créés</span>
                                        <span className="text-lg font-bold text-emerald-500">{summary.created}</span>
                                    </div>
                                    <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded-2xl text-center">
                                        <span className="text-xs text-blue-500 font-medium block">Mis à jour</span>
                                        <span className="text-lg font-bold text-blue-500">{summary.updated || 0}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Status: Completed */}
                        {status === 'completed' && (
                            <div className="space-y-6">
                                <div className="flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl">
                                    <div className="p-3 bg-emerald-500/20 text-emerald-500 rounded-xl">
                                        <UserCheck size={28} />
                                    </div>
                                    <div>
                                        <h4 className="text-emerald-500 font-bold text-base">Importation terminée !</h4>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            La liste des membres a été mise à jour et synchronisée.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-muted/50 border border-border p-4 rounded-2xl text-center">
                                        <p className="text-xs text-muted-foreground mb-1">Membres créés</p>
                                        <p className="text-2xl font-bold text-emerald-500">{summary.created}</p>
                                    </div>
                                    <div className="bg-muted/50 border border-border p-4 rounded-2xl text-center">
                                        <p className="text-xs text-muted-foreground mb-1">Mis à jour</p>
                                        <p className="text-2xl font-bold text-blue-500">{summary.updated || 0}</p>
                                    </div>
                                    <div className="bg-muted/50 border border-border p-4 rounded-2xl text-center">
                                        <p className="text-xs text-muted-foreground mb-1">Erreurs</p>
                                        <p className="text-2xl font-bold text-red-500">{summary.errors}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Status: Error */}
                        {status === 'error' && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-start gap-3">
                                <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="text-red-500 font-bold text-sm">Échec de l'importation</h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {summary.errorMessage || "Impossible de lire ou de parser le fichier CSV. Vérifiez son format."}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 bg-muted/30 border-t border-border flex justify-end">
                        {status === 'completed' || status === 'error' ? (
                            <button
                                onClick={() => {
                                    if (onFinish) onFinish();
                                    onClose();
                                }}
                                className="px-6 py-2.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
                            >
                                <RefreshCw size={16} />
                                <span>Fermer & Actualiser</span>
                            </button>
                        ) : (
                            <span className="text-xs text-muted-foreground italic py-2">
                                Veuillez patienter pendant l'importation...
                            </span>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ImportCSVModal;
